import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  Eye,
  MessageSquare,
  MoreHorizontal,
  ArrowUp,
  Trash2,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { AdminPageHeader, AdminSheet, AdminEmptyState, AdminTableSkeleton, DeleteConfirmDialog } from './shared';
import {
  StandardProposal,
  useProposals,
  useProposalComments,
  useUpdateProposalStatus,
  useDeleteProposal,
} from '@/hooks/useProposals';
import { useAdfElements } from '@/hooks/useAdfStandard';
import { toast } from 'sonner';
import { Lightbulb } from 'lucide-react';

type StatusTab = 'pending' | 'under_review' | 'approved' | 'rejected' | 'implemented' | 'all';

import {
  PROPOSAL_STATUS_LABELS,
  PROPOSAL_STATUS_COLORS,
  PROPOSAL_TYPE_LABELS,
  PROPOSAL_TYPE_COLORS,
} from '@/lib/admin/constants';

// Helper component to display linked ADF element
function AdfElementLink({ elementId }: { elementId: string }) {
  const { data: elements = [] } = useAdfElements();
  const element = elements.find(e => e.id === elementId);
  
  if (!element) {
    return <span className="text-muted-foreground">Unknown element</span>;
  }

  return (
    <Link 
      to={`/adf-standard?element=${element.slug}`}
      className="inline-flex items-center gap-1.5 text-primary hover:underline"
      target="_blank"
    >
      <code className="bg-muted px-1 rounded text-xs">&lt;{element.tag_name}&gt;</code>
      <span>{element.display_name}</span>
      <ExternalLink className="h-3 w-3" />
    </Link>
  );
}

export default function ProposalsManager() {
  const [activeTab, setActiveTab] = useState<StatusTab>('pending');
  const [selectedProposal, setSelectedProposal] = useState<StandardProposal | null>(null);
  const [deleteProposal, setDeleteProposal] = useState<StandardProposal | null>(null);

  const { data: proposals = [], isLoading } = useProposals({
    status: activeTab !== 'all' ? activeTab : undefined,
    sortBy: 'date',
    isAdmin: true, // Query base table for full data including emails
  });

  const deleteMutation = useDeleteProposal();

  const handleDelete = async () => {
    if (!deleteProposal) return;
    try {
      await deleteMutation.mutateAsync(deleteProposal.id);
      toast.success('Proposal deleted');
      setDeleteProposal(null);
    } catch {
      toast.error('Failed to delete proposal');
    }
  };

  const pendingCount = proposals.filter((p) => p.status === 'pending').length;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Proposals"
        count={proposals.length}
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as StatusTab)}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            Pending
            {pendingCount > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="under_review">Under Review</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="implemented">Implemented</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <AdminTableSkeleton columns={6} rows={5} />
      ) : proposals.length === 0 ? (
        <AdminEmptyState
          icon={Lightbulb}
          title="No proposals"
          description={
            activeTab === 'all'
              ? 'No proposals have been submitted yet.'
              : `No ${PROPOSAL_STATUS_LABELS[activeTab]?.toLowerCase()} proposals.`
          }
        />
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Submitter</TableHead>
                <TableHead className="text-center">Votes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.map((proposal) => (
                <TableRow 
                  key={proposal.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedProposal(proposal)}
                >
                  <TableCell className="font-medium max-w-[200px] truncate">
                    {proposal.title}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={PROPOSAL_TYPE_COLORS[proposal.proposal_type] || ''}
                    >
                      {PROPOSAL_TYPE_LABELS[proposal.proposal_type] || proposal.proposal_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{proposal.submitter_name}</div>
                      {proposal.submitter_organization && (
                        <div className="text-muted-foreground text-xs">
                          {proposal.submitter_organization}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <ArrowUp className="h-3 w-3" />
                      {proposal.vote_count}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={PROPOSAL_STATUS_COLORS[proposal.status]}>
                      {PROPOSAL_STATUS_LABELS[proposal.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDistanceToNow(new Date(proposal.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedProposal(proposal)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setDeleteProposal(proposal)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail Sheet */}
      <AdminProposalDetail
        proposal={selectedProposal}
        open={!!selectedProposal}
        onOpenChange={(open) => !open && setSelectedProposal(null)}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={!!deleteProposal}
        onOpenChange={(open) => !open && setDeleteProposal(null)}
        itemType="proposal"
        itemName={deleteProposal?.title}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}

interface AdminProposalDetailProps {
  proposal: StandardProposal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AdminProposalDetail({ proposal, open, onOpenChange }: AdminProposalDetailProps) {
  const [adminNotes, setAdminNotes] = useState(proposal?.admin_notes || '');
  const [newStatus, setNewStatus] = useState<StandardProposal['status'] | ''>('');

  const updateMutation = useUpdateProposalStatus();
  const { data: comments = [] } = useProposalComments(proposal?.id || '', true); // Admin access for full data

  const handleUpdateStatus = async () => {
    if (!proposal || !newStatus) return;
    try {
      await updateMutation.mutateAsync({
        proposalId: proposal.id,
        status: newStatus,
        admin_notes: adminNotes || undefined,
      });
      toast.success('Proposal updated');
      onOpenChange(false);
    } catch {
      toast.error('Failed to update proposal');
    }
  };

  // Reset form when proposal changes
  if (proposal && adminNotes !== proposal.admin_notes && !newStatus) {
    setAdminNotes(proposal.admin_notes || '');
  }

  if (!proposal) return null;

  const isAdfType = ['adf_element', 'adf_mapping', 'adf_attribute'].includes(proposal.proposal_type);

  return (
    <AdminSheet
      open={open}
      onOpenChange={onOpenChange}
      title={proposal.title}
      description={`${PROPOSAL_TYPE_LABELS[proposal.proposal_type] || proposal.proposal_type} proposal - ${PROPOSAL_STATUS_LABELS[proposal.status]}`}
      onSave={handleUpdateStatus}
      isSaving={updateMutation.isPending}
      saveLabel="Update Status"
      saveDisabled={!newStatus}
      size="xl"
    >
      <div className="space-y-6">
        {/* Status badges */}
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={PROPOSAL_TYPE_COLORS[proposal.proposal_type] || ''}
          >
            {PROPOSAL_TYPE_LABELS[proposal.proposal_type] || proposal.proposal_type}
          </Badge>
          <Badge variant="outline" className={PROPOSAL_STATUS_COLORS[proposal.status]}>
            {PROPOSAL_STATUS_LABELS[proposal.status]}
          </Badge>
        </div>

        {/* Meta */}
        <div className="text-sm space-y-1 text-muted-foreground">
          <div>
            <strong>Submitter:</strong> {proposal.submitter_name}
            {proposal.submitter_organization && ` (${proposal.submitter_organization})`}
          </div>
          <div>
            <strong>Email:</strong> {proposal.submitter_email}
          </div>
          <div>
            <strong>Submitted:</strong> {format(new Date(proposal.created_at), 'PPP')}
          </div>
          <div>
            <strong>Votes:</strong> {proposal.vote_count}
          </div>
        </div>

        <Separator />

        {/* Description & Justification */}
        <div>
          <h4 className="font-medium mb-2">Description</h4>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {proposal.description}
          </p>
        </div>

        <div>
          <h4 className="font-medium mb-2">Justification</h4>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {proposal.justification}
          </p>
        </div>

        {/* Field/Enum Details */}
        {proposal.proposal_type === 'field' && proposal.proposed_field_name && (
          <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-1">
            <div><strong>Field Name:</strong> {proposal.proposed_field_name}</div>
            {proposal.proposed_field_type && (
              <div><strong>Type:</strong> {proposal.proposed_field_type}</div>
            )}
            {proposal.proposed_field_format && (
              <div><strong>Format:</strong> {proposal.proposed_field_format}</div>
            )}
            <div><strong>Required:</strong> {proposal.proposed_field_required ? 'Yes' : 'No'}</div>
          </div>
        )}

        {proposal.proposal_type === 'enum_value' && (
          <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-1">
            <div><strong>Enum Value:</strong> {proposal.proposed_enum_value}</div>
            <div><strong>Target Field:</strong> {proposal.target_field_name}</div>
          </div>
        )}

        {/* ADF Element Details */}
        {proposal.proposal_type === 'adf_element' && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-sm space-y-2">
            <h4 className="font-medium text-amber-600 dark:text-amber-400">Proposed ADF Element</h4>
            {proposal.proposed_field_name && (
              <div>
                <strong>Tag Name:</strong>{' '}
                <code className="bg-background px-1.5 py-0.5 rounded">&lt;{proposal.proposed_field_name}&gt;</code>
              </div>
            )}
            {proposal.target_adf_element_id && (
              <div>
                <strong>Parent Element:</strong>{' '}
                <AdfElementLink elementId={proposal.target_adf_element_id} />
              </div>
            )}
            <div><strong>Required:</strong> {proposal.proposed_field_required ? 'Yes' : 'No'}</div>
          </div>
        )}

        {/* ADF Mapping Details */}
        {proposal.proposal_type === 'adf_mapping' && (
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 text-sm space-y-2">
            <h4 className="font-medium text-cyan-600 dark:text-cyan-400">Proposed ADF Mapping</h4>
            {proposal.proposed_field_name && (
              <div>
                <strong>Field Name:</strong>{' '}
                <code className="bg-background px-1.5 py-0.5 rounded">{proposal.proposed_field_name}</code>
              </div>
            )}
            {proposal.target_adf_element_id && (
              <div>
                <strong>Target Element:</strong>{' '}
                <AdfElementLink elementId={proposal.target_adf_element_id} />
              </div>
            )}
            <div><strong>Required:</strong> {proposal.proposed_field_required ? 'Yes' : 'No'}</div>
          </div>
        )}

        {/* ADF Attribute Details */}
        {proposal.proposal_type === 'adf_attribute' && (
          <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4 text-sm space-y-2">
            <h4 className="font-medium text-rose-600 dark:text-rose-400">Proposed ADF Attribute</h4>
            {proposal.proposed_field_name && (
              <div>
                <strong>Attribute Name:</strong>{' '}
                <code className="bg-background px-1.5 py-0.5 rounded">{proposal.proposed_field_name}</code>
              </div>
            )}
            {proposal.target_adf_element_id && (
              <div>
                <strong>Target Element:</strong>{' '}
                <AdfElementLink elementId={proposal.target_adf_element_id} />
              </div>
            )}
            {proposal.proposed_enum_value && (
              <div><strong>Valid Values:</strong> {proposal.proposed_enum_value}</div>
            )}
          </div>
        )}

        <Separator />

        {/* Comments */}
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Comments ({comments.length})
          </h4>
          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No comments yet.</p>
          ) : (
            <div className="space-y-3">
              {comments.map((comment) => (
                <div key={comment.id} className="text-sm border-l-2 pl-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {comment.commenter_name}
                    </span>
                    {comment.is_admin && (
                      <Badge variant="secondary" className="text-xs py-0">Admin</Badge>
                    )}
                    <span>•</span>
                    <span>
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="mt-1">{comment.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* Admin Actions */}
        <div className="space-y-4">
          <h4 className="font-medium">Update Status</h4>

          <div className="space-y-2">
            <Label>New Status</Label>
            <Select value={newStatus} onValueChange={(v) => setNewStatus(v as StandardProposal['status'])}>
              <SelectTrigger>
                <SelectValue placeholder="Select new status..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="implemented">Implemented</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Admin Notes</Label>
            <Textarea
              placeholder="Add notes visible to the submitter..."
              rows={4}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
            />
          </div>
        </div>
      </div>
    </AdminSheet>
  );
}